import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Button } from "../../components/Button";
import { Check, Plus, ChevronRight, MoreVertical, Info } from "lucide-react";
import { ColorSectionProps } from "./types";
import { ColorPicker, ColorSwatch } from "../../components/ColorPicker";
import colors from "../../colors/colors";

/**
 * Default theme colors that are added to every new theme
 * These match the backend DEFAULT_THEME_COLORS and cannot be deleted
 */
export const DEFAULT_THEME_COLORS: Record<string, string> = {
  // Core colors
  primary: "#181d56",
  secondary: "#ffffff",
  tertiary: "#7e8fa8",
  accent: "#ffffff",
  background: "#ffffff",
  transparent: "#00000000",
  // Text colors
  textPrimary: "#181d56",
  textSecondary: "#181d56",
  textTertiary: "#7e8fa8",
  // UI colors
  borderColor: "#DCDCDC",
  buttonText: "#ffffff",
  // Semantic colors
  link: "#5572b7",
  success: "#71c285",
  error: "#ff534a",
  warning: "#ffbd32",
  info: "#5572b7",
};

/**
 * Array of default theme color keys (colors that cannot be deleted)
 */
export const DEFAULT_THEME_COLOR_KEYS = Object.keys(DEFAULT_THEME_COLORS);

/**
 * Normalize a color value to 8-char RGBA hex
 * Handles both 6-char (#RRGGBB) and 8-char (#RRGGBBAA) formats
 */
const normalizeToRgba = (color: string): string => {
  if (!color) return "#000000ff";
  const normalized = color.startsWith("#") ? color : `#${color}`;
  // If it's already 8-char, return as-is
  if (normalized.length === 9) {
    return normalized;
  }
  // If it's 6-char, add full opacity
  if (normalized.length === 7) {
    return `${normalized}ff`;
  }
  // For other lengths, pad to 8-char
  return normalized.padEnd(9, "f").slice(0, 9);
};

/**
 * Normalize a color value for storage (remove `ff` alpha if fully opaque)
 * Returns 6-char hex if alpha is `ff`, otherwise 8-char hex
 */
const normalizeForStorage = (color: string): string => {
  const rgba = normalizeToRgba(color);
  // If alpha is `ff` (fully opaque), return 6-char hex
  if (rgba.length === 9 && rgba.slice(7, 9).toLowerCase() === "ff") {
    return rgba.slice(0, 7);
  }
  // Otherwise return 8-char hex
  return rgba;
};

/**
 * Display hex - show 6-char if alpha is `ff`, otherwise show 8-char
 */
const displayHex = (color: string): string => {
  const normalized = normalizeToRgba(color || "#000000");
  // If alpha is `ff` (fully opaque), show 6-char hex
  if (
    normalized.length === 9 &&
    normalized.slice(7, 9).toLowerCase() === "ff"
  ) {
    return normalized.slice(0, 7);
  }
  // Otherwise show 8-char hex
  return normalized;
};

export const ColorSection: React.FC<ColorSectionProps> = ({
  themeColors,
  isExpanded,
  onToggle,
  isLoading,
  isAddingColor,
  setIsAddingColor,
  newColorKey,
  setNewColorKey,
  newColorValue,
  setNewColorValue,
  onAddColor,
  onUpdateColor,
  onRenameColor,
  onDeleteColor,
  studioLockedColorKeys,
}) => {
  // Track which color picker popover is open (colorKey or null)
  const [openColorPicker, setOpenColorPicker] = useState<string | null>(null);
  // Refs to track swatch button positions for portal positioning
  const swatchRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [popoverPosition, setPopoverPosition] = useState({
    top: 0,
    left: 0,
    openAbove: false,
  });

  // Track which color row is in edit mode (colorKey or null)
  const [editingRow, setEditingRow] = useState<string | null>(null);
  // Local values while editing (not saved to API until user clicks Save)
  const [localValues, setLocalValues] = useState<{
    name: string;
    value: string;
  } | null>(null);

  // Track which dropdown menu is open (colorKey or null)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  // Ref for dropdown menu positioning
  const dropdownRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
  });

  // Estimated height of the color picker popover (including padding)
  const POPOVER_HEIGHT = 300;

  // Update popover position when opening
  useEffect(() => {
    if (openColorPicker && swatchRefs.current[openColorPicker]) {
      const rect = swatchRefs.current[openColorPicker]!.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Open above if not enough space below and more space above
      const openAbove = spaceBelow < POPOVER_HEIGHT && spaceAbove > spaceBelow;

      setPopoverPosition({
        top: openAbove ? rect.top - 8 : rect.bottom + 8,
        left: rect.left + rect.width / 2,
        openAbove,
      });
    }
  }, [openColorPicker]);

  // Click-away listener to close color picker popover
  useEffect(() => {
    if (!openColorPicker) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest(".bc-theme-color-popover") &&
        !target.closest(".bc-theme-color-swatch")
      ) {
        setOpenColorPicker(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openColorPicker]);

  // Update dropdown position when opening
  useEffect(() => {
    if (openDropdown && dropdownRefs.current[openDropdown]) {
      const rect = dropdownRefs.current[openDropdown]!.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.right,
      });
    }
  }, [openDropdown]);

  // Click-away listener to close dropdown menu
  useEffect(() => {
    if (!openDropdown) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest(".bc-theme-color-dropdown") &&
        !target.closest(".bc-theme-color-menu-btn")
      ) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  // Handler to enter edit mode
  const handleStartEdit = (key: string, value: string) => {
    setEditingRow(key);
    setLocalValues({ name: key, value: normalizeToRgba(value) });
    setOpenDropdown(null);
    setOpenColorPicker(null);
  };

  // Handler to cancel edit mode
  const handleCancelEdit = () => {
    setEditingRow(null);
    setLocalValues(null);
    setOpenColorPicker(null);
  };

  // Handler to save edit
  const handleSaveEdit = async (originalKey: string) => {
    if (!localValues) return;

    const { name: newName, value: newValue } = localValues;
    const normalizedValue = normalizeForStorage(newValue);

    // Validate hex value
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/;
    if (!hexRegex.test(normalizedValue)) {
      // Invalid color, don't save
      return;
    }

    // Check if name changed
    if (newName.trim() && newName.trim() !== originalKey && onRenameColor) {
      await onRenameColor(originalKey, newName.trim());
    }

    // Check if value changed
    const originalNormalized = normalizeForStorage(themeColors[originalKey]);
    if (normalizedValue !== originalNormalized) {
      // Use the potentially renamed key
      const keyToUpdate = newName.trim() || originalKey;
      await onUpdateColor(keyToUpdate, normalizedValue);
    }

    // Exit edit mode
    setEditingRow(null);
    setLocalValues(null);
    setOpenColorPicker(null);
  };

  // Update local color value (without API call)
  const handleLocalColorChange = (newValue: string) => {
    if (localValues) {
      setLocalValues({ ...localValues, value: newValue });
    }
  };

  // Update local name value (without API call)
  const handleLocalNameChange = (newName: string) => {
    if (localValues) {
      setLocalValues({ ...localValues, name: newName });
    }
  };

  const renderColorSwatch = (
    colorKey: string,
    colorValue: string,
    onChange: (value: string) => void,
    editable: boolean,
  ) => {
    const isOpen = openColorPicker === colorKey;
    const rgbaValue = normalizeToRgba(colorValue);
    const canOpen = editable && !isLoading;

    return (
      <>
        {canOpen ? (
          <button
            ref={(el) => {
              swatchRefs.current[colorKey] = el;
            }}
            type="button"
            onClick={() => setOpenColorPicker(isOpen ? null : colorKey)}
            className="bc-theme-color-swatch"
            style={{
              width: "32px",
              height: "32px",
              border: `2px solid ${colors.border_glass}`,
              borderRadius: "6px",
              cursor: "pointer",
              padding: "2px",
              transition: "all 0.15s ease",
              backgroundColor: colors.white,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
            aria-label="Open color picker"
          >
            <ColorSwatch
              color={rgbaValue}
              style={{ width: "100%", height: "100%" }}
              borderRadius={4}
            />
          </button>
        ) : (
          <div
            style={{
              width: "32px",
              height: "32px",
              border: `2px solid ${colors.border_glass}`,
              borderRadius: "6px",
              padding: "2px",
              backgroundColor: colors.white,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ColorSwatch
              color={rgbaValue}
              style={{ width: "100%", height: "100%" }}
              borderRadius={4}
            />
          </div>
        )}

        {isOpen &&
          canOpen &&
          ReactDOM.createPortal(
            <div
              className="bc-theme-color-popover"
              style={{
                position: "fixed",
                top: popoverPosition.top,
                left: popoverPosition.left,
                transform: popoverPosition.openAbove
                  ? "translate(-50%, -100%)"
                  : "translateX(-50%)",
                zIndex: 1000002,
                backgroundColor: colors.white,
                borderRadius: "12px",
                padding: "12px",
                boxShadow: `0 8px 24px ${colors.shadow_medium}, 0 0 0 1px ${colors.border_glass}`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <ColorPicker value={rgbaValue} onChange={onChange} />
            </div>,
            document.body,
          )}
      </>
    );
  };

  return (
    <div className="bc-theme-editor-section">
      <style>{`
        .bc-theme-color-swatch:hover:not(:disabled) {
          border-color: ${colors.gray_border};
          transform: scale(1.05);
        }
        .bc-theme-color-menu-btn:hover:not(:disabled) {
          background: ${colors.gray_light} !important;
        }
        .bc-theme-editor-row-container {
          display: flex;
          flex-direction: column;
        }
      `}</style>

      <div className="bc-theme-editor-section-header" onClick={onToggle}>
        <h3 className="bc-theme-editor-section-title">
          Colors
          <span className="bc-theme-editor-section-count">
            {Object.keys(themeColors).length}
          </span>
        </h3>
        <ChevronRight
          size={16}
          className={`bc-theme-editor-section-chevron ${isExpanded ? "expanded" : ""}`}
        />
      </div>

      {isExpanded && (
        <div className="bc-theme-editor-section-content">
          {isLoading && Object.keys(themeColors).length === 0 ? (
            <div className="bc-theme-editor-loading">Loading colors...</div>
          ) : (
            <div className="bc-theme-editor-list">
              {Object.entries(themeColors).map(([key, value]) => {
                const isEditing = editingRow === key;
                const isSpecialColor = DEFAULT_THEME_COLOR_KEYS.includes(key);
                const isStudioLocked = studioLockedColorKeys?.has(key) ?? false;
                const isDropdownOpen = openDropdown === key;

                // When editing, use local values; otherwise use actual values
                const displayName =
                  isEditing && localValues ? localValues.name : key;
                const displayValue =
                  isEditing && localValues
                    ? displayHex(localValues.value)
                    : displayHex(value);
                const colorValue =
                  isEditing && localValues ? localValues.value : value;

                return (
                  <div key={key} className="bc-theme-editor-row-container">
                    <div className="bc-theme-editor-row">
                      {/* Name input - read-only unless in edit mode */}
                      <input
                        type="text"
                        className="bc-theme-editor-input"
                        value={displayName}
                        onChange={(e) =>
                          isEditing && handleLocalNameChange(e.target.value)
                        }
                        readOnly={!isEditing}
                        disabled={isLoading}
                        style={{ cursor: isEditing ? "text" : "default" }}
                      />

                      {/* Color swatch - only clickable in edit mode */}
                      {renderColorSwatch(
                        key,
                        colorValue,
                        handleLocalColorChange,
                        isEditing, // only editable when in edit mode
                      )}

                      {/* Hex value - read-only unless in edit mode */}
                      {isEditing ? (
                        <input
                          type="text"
                          className="bc-theme-editor-input"
                          value={displayValue}
                          onChange={(e) => {
                            const newHex = e.target.value;
                            if (localValues) {
                              setLocalValues({ ...localValues, value: newHex });
                            }
                          }}
                          disabled={isLoading}
                          placeholder="#000000 or #000000ff"
                          maxLength={9}
                          style={{
                            width: "85px",
                            textAlign: "right",
                            fontFamily:
                              "'SF Mono', 'Menlo', 'Monaco', 'Courier New', monospace",
                          }}
                        />
                      ) : (
                        <span
                          className="bc-theme-editor-color-value"
                          style={{ cursor: "default" }}
                        >
                          {displayValue}
                        </span>
                      )}

                      {/* Studio lock indicator */}
                      {isStudioLocked && !isEditing && (
                        <span
                          title="Edit this color from Concierge Studio"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            color: colors.blue,
                            padding: "4px",
                            cursor: "help",
                          }}
                        >
                          <Info size={14} />
                        </span>
                      )}

                      {/* 3-dot menu button (only shown when not in edit mode and not studio-locked) */}
                      {!isEditing && !isStudioLocked && (
                        <button
                          ref={(el) => {
                            dropdownRefs.current[key] = el;
                          }}
                          className="bc-theme-color-menu-btn"
                          onClick={() =>
                            setOpenDropdown(isDropdownOpen ? null : key)
                          }
                          disabled={isLoading}
                          aria-label="Color options"
                          style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: colors.gray_medium,
                            transition: "all 0.15s ease",
                          }}
                        >
                          <MoreVertical size={16} />
                        </button>
                      )}

                      {/* Dropdown menu */}
                      {isDropdownOpen &&
                        ReactDOM.createPortal(
                          <div
                            className="bc-theme-color-dropdown"
                            style={{
                              position: "fixed",
                              top: dropdownPosition.top,
                              left: dropdownPosition.left,
                              transform: "translateX(-100%)",
                              zIndex: 1000003,
                              backgroundColor: colors.white,
                              borderRadius: "8px",
                              padding: "4px",
                              boxShadow: `0 4px 12px ${colors.shadow_medium}, 0 0 0 1px ${colors.border_glass}`,
                              minWidth: "100px",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              className="bc-theme-dropdown-item"
                              onClick={() => handleStartEdit(key, value)}
                              style={{
                                display: "block",
                                width: "100%",
                                padding: "8px 12px",
                                border: "none",
                                background: "transparent",
                                textAlign: "left",
                                cursor: "pointer",
                                borderRadius: "4px",
                                fontSize: "13px",
                                color: colors.primary,
                                transition: "background 0.15s ease",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background =
                                  colors.gray_light)
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background =
                                  "transparent")
                              }
                            >
                              Edit
                            </button>
                            <button
                              className="bc-theme-dropdown-item"
                              onClick={() => {
                                setOpenDropdown(null);
                                onDeleteColor(key);
                              }}
                              disabled={isSpecialColor}
                              style={{
                                display: "block",
                                width: "100%",
                                padding: "8px 12px",
                                border: "none",
                                background: "transparent",
                                textAlign: "left",
                                cursor: isSpecialColor
                                  ? "not-allowed"
                                  : "pointer",
                                borderRadius: "4px",
                                fontSize: "13px",
                                color: isSpecialColor
                                  ? colors.gray_medium
                                  : colors.red,
                                opacity: isSpecialColor ? 0.5 : 1,
                                transition: "background 0.15s ease",
                              }}
                              onMouseEnter={(e) => {
                                if (!isSpecialColor) {
                                  e.currentTarget.style.background =
                                    colors.gray_light;
                                }
                              }}
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background =
                                  "transparent")
                              }
                            >
                              Delete
                            </button>
                          </div>,
                          document.body,
                        )}
                    </div>

                    {/* Save/Cancel buttons shown below row when in edit mode */}
                    {isEditing && (
                      <div
                        className="bc-theme-editor-row-actions"
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginTop: "8px",
                          marginLeft: "auto",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          variant="primary"
                          size="sm"
                          leftIcon={<Check size={12} />}
                          onClick={() => handleSaveEdit(key)}
                          disabled={isLoading}
                        >
                          Save
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleCancelEdit}
                          disabled={isLoading}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}

              {isAddingColor ? (
                <div className="bc-theme-editor-row">
                  <input
                    type="text"
                    className="bc-theme-editor-input"
                    placeholder="Color name"
                    value={newColorKey}
                    onChange={(e) => setNewColorKey(e.target.value)}
                    disabled={isLoading}
                    autoFocus
                  />
                  {renderColorSwatch(
                    "__new_color__",
                    newColorValue,
                    setNewColorValue,
                    true, // new color is editable
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={<Check size={12} />}
                    onClick={onAddColor}
                    disabled={isLoading || !newColorKey.trim()}
                  >
                    Save
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setIsAddingColor(false);
                      setNewColorKey("");
                      setNewColorValue("#000000ff");
                    }}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Plus size={12} />}
                  onClick={() => setIsAddingColor(true)}
                  disabled={isLoading}
                >
                  Add Color
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
